import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CloseOutlined, ReloadOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux/es/hooks/useSelector';
import {
  setActiveMenu,
  addMenu,
  removeFromMenu,
  setRefetch,
} from '../redux/slices/menu';
import { shallowEqual, useDispatch } from 'react-redux';
import { Tabs } from 'antd';
import { useTranslation } from 'react-i18next';
const { TabPane } = Tabs;

const TabMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search, state: locationState } = useLocation();
  const dispatch = useDispatch();
  const menu = useSelector((state) => state.menu, shallowEqual);
  const hydratedFromUrlRef = useRef(false);
  /* True from the first render's adopt dispatch until that tab is active. */
  const adoptingUrlRef = useRef(false);

  const selectTab = (item) => {
    dispatch(setActiveMenu(item));
    navigate(`/${item.url}`);
  };

  const deleteTab = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    const index = menu.menuItems.indexOf(item);
    if (!index) {
      toast.warning(t('menu.tab.does.not.close'));
      return;
    }

    if (menu.activeMenu?.id !== item.id) {
      dispatch(removeFromMenu({ ...item, nextUrl: menu.activeMenu.url }));
      return;
    }
    const newPath = menu.menuItems.filter((el) => el.url !== item.url);
    const isHaveOtherTabs = newPath.length - 1 > index;
    let currentPath;
    if (isHaveOtherTabs) {
      currentPath = newPath[index];
    } else {
      currentPath = newPath[newPath.length - 1];
    }
    dispatch(removeFromMenu({ ...item, nextUrl: currentPath.url }));
    navigate(`/${currentPath.url}`);
  };

  const reloadPage = (event, item) => {
    event.preventDefault();
    event.stopPropagation();
    dispatch(setRefetch(item));
    if (item.id !== menu.activeMenu.id) {
      navigate(`/${item.url}`);
    }
  };

  // On first mount, adopt the URL the browser actually loaded (a refresh,
  // a bookmark, or a deep link) as the active tab — Redux state starts on
  // "dashboard" on every page reload, and the previous effect fought that
  // by navigating everyone back to /dashboard whenever it disagreed with
  // pathname. Now URL is the source of truth on hydration; only later state
  // changes (a click on another tab) drive navigate().
  useEffect(() => {
    if (hydratedFromUrlRef.current) return;
    hydratedFromUrlRef.current = true;
    const normalizedPath = pathname.replace(/^\/+/, '');
    if (!normalizedPath || normalizedPath === 'dashboard') return;
    const activeUrl = menu.activeMenu?.url;
    const activePath = activeUrl ? String(activeUrl).split('?')[0] : '';
    if (normalizedPath.startsWith(activePath) && activePath !== 'dashboard') return;
    // Adopt the current URL — either update an existing menu entry that
    // matches this path, or push a new one so tabs stay in sync with what
    // the browser is showing. Query string / search is preserved via `url`
    // so pagination and filters carried in the URL survive too.
    const urlWithSearch = `${normalizedPath}${search || ''}`;
    adoptingUrlRef.current = true;
    const existing = menu.menuItems.find(
      (item) => String(item.url).split('?')[0] === normalizedPath,
    );
    if (existing) {
      dispatch(setActiveMenu({ ...existing, url: urlWithSearch }));
    } else {
      dispatch(
        addMenu({
          id: normalizedPath,
          url: urlWithSearch,
          name: normalizedPath.split('/').pop() || normalizedPath,
          data: {},
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After hydration, keep the URL in sync with the *active tab's* url when
  // that url changes (e.g. the user clicks another tab). Not the other way
  // round — otherwise typing a URL in the bar would fight tab state.
  useEffect(() => {
    if (!hydratedFromUrlRef.current) return;
    const activeUrl = menu.activeMenu?.url;
    if (!activeUrl) return;
    const activePath = String(activeUrl).split('?')[0];
    const normalizedPath = pathname.replace(/^\/+/, '');
    /*
      The load that is adopting its own URL.

      This effect runs in the same commit as the one above, before its
      dispatch has landed, so the active tab still reads "dashboard". Syncing
      then sent every refresh, bookmark and "open in new tab" to /dashboard
      and straight back again. Hold off until the adopted tab arrives.
    */
    if (adoptingUrlRef.current) {
      if (normalizedPath.startsWith(activePath)) adoptingUrlRef.current = false;
      return;
    }
    if (normalizedPath.startsWith(activePath)) return;
    navigate(
      { pathname: `/${activePath}`, search },
      { replace: true, state: locationState },
    );
  }, [menu.activeMenu?.url, navigate, pathname, search, locationState]);

  return (
    <div className='navbar-default'>
      <Tabs activeKey={menu.activeMenu?.url} type='card'>
        {menu.menuItems.map((item) => (
          <TabPane
            tab={
              <div onClick={() => selectTab(item)} className='nav-link'>
                <ReloadOutlined
                  onClick={(event) => reloadPage(event, item)}
                  className='reload-button'
                />
                <span className='nav-text'> {t(item.name)}</span>
                <CloseOutlined
                  onClick={(event) => deleteTab(event, item)}
                  className='close-button'
                />
              </div>
            }
            key={item.url}
          />
        ))}
      </Tabs>
    </div>
  );
};

export default TabMenu;
